/**
 * The object menus (TMMAPP-0072 / 0089; TMMAPP-0099, visual contract §11). A labelled kebab opens a
 * `Menu` of an object's secondary actions; `children` is a render function so an item can close
 * the menu. The prototype draws two styles, and every object menu is one of them:
 *
 * - `page` — the kebab beside a page `h1` (P:2472 / 2506): min-width 200, padding 4 0; the object's
 *   name as an `overline` header (padding 8 16 10); items 13px, padding 10 16, with a `line` rule
 *   above every item.
 * - `card` — the kebab on a card or a row (P:2600–2607): 184 wide, padding 6 0; the name at 12px,
 *   not uppercase, padding 6 16 8 over a `line` rule; items 14px, padding 9 16, no rules.
 *
 * Both are `surfaceRaised` at radius `control` with the dialog elevation (the theme's `MuiMenu`),
 * carry no icons, and paint a destructive item `errorText` (`destructiveItemSx`). A menu that
 * builds its own `Menu` (a row menu anchored by its list) passes `objectMenuSlotProps`
 * (`objectMenu.ts`) and renders `MenuObjectHeader` with the same variant.
 */
import { useId, useState, type ReactNode } from 'react';
import { Box, IconButton, Menu, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { KebabIcon } from '@/components/icons';
import { raisedLine } from '@/theme/raisedLine';
import { resolveTokens } from '@/theme/theme';
import { objectMenuSlotProps, pageKebabSx, type ObjectMenuVariant } from './objectMenu';

/**
 * Non-focusable object name above a kebab's items (TMMAPP-0089). `aria-hidden` so the button's
 * accessible name stays "Actions for {name}".
 */
export function MenuObjectHeader({
  title,
  variant = 'card',
}: {
  title: string;
  variant?: ObjectMenuVariant;
}) {
  const tokens = resolveTokens(useTheme());
  if (title.trim().length === 0) return null;
  if (variant === 'page') {
    return (
      <Box aria-hidden data-menu-header="page" sx={{ p: '8px 16px 10px', pointerEvents: 'none' }}>
        <Typography variant="overline" component="div" sx={{ overflowWrap: 'anywhere' }}>
          {title}
        </Typography>
      </Box>
    );
  }
  return (
    <Box
      aria-hidden
      data-menu-header="card"
      sx={{
        p: '6px 16px 8px',
        pointerEvents: 'none',
        borderBottom: `1px solid ${raisedLine(tokens)}`,
      }}
    >
      <Typography
        variant="caption"
        component="div"
        noWrap
        sx={{ fontSize: 12, lineHeight: '18px', color: tokens.textLabel('surfaceRaised') }}
      >
        {title}
      </Typography>
    </Box>
  );
}

/**
 * An object menu anchored by its caller (a row's kebab, a calendar chip): MUI's `Menu` in one of
 * the two styles, with the object's name as its header.
 */
export function ObjectMenu({
  variant = 'card',
  title,
  anchorEl,
  open,
  onClose,
  id,
  children,
}: {
  variant?: ObjectMenuVariant;
  title?: string | undefined;
  anchorEl: HTMLElement | null | undefined;
  open: boolean;
  onClose: () => void;
  id?: string;
  children: ReactNode;
}) {
  const tokens = resolveTokens(useTheme());
  return (
    <Menu
      {...(id === undefined ? {} : { id })}
      anchorEl={anchorEl ?? null}
      open={open}
      onClose={onClose}
      slotProps={objectMenuSlotProps(tokens, variant)}
    >
      {title !== undefined ? <MenuObjectHeader title={title} variant={variant} /> : null}
      {children}
    </Menu>
  );
}

export interface OverflowMenuProps {
  /** The button's `aria-label`; defaults to the catalog's "More actions". */
  label?: string;
  /** The object's name, shown as a non-focusable header. */
  title?: string;
  /** `page` beside an `h1`, `card` on a card or a row. */
  variant?: ObjectMenuVariant;
  children: (close: () => void) => ReactNode;
}

export function OverflowMenu({ label, title, variant = 'card', children }: OverflowMenuProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const menuId = useId();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);
  const page = variant === 'page';
  return (
    <>
      <IconButton
        aria-label={label ?? t('pageHeader.moreActions')}
        aria-haspopup="menu"
        aria-controls={anchor !== null ? menuId : undefined}
        aria-expanded={anchor !== null}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={page ? pageKebabSx(tokens) : undefined}
      >
        <KebabIcon size={18} />
      </IconButton>
      <ObjectMenu
        id={menuId}
        variant={variant}
        title={title}
        anchorEl={anchor}
        open={anchor !== null}
        onClose={close}
      >
        {children(close)}
      </ObjectMenu>
    </>
  );
}
